import BackendService from '../../../services/admin/BackendService';
import type Investor from '../../../models/Investor';
import {
  FundOpportunityWithInvestor,
  PIPELINE_STATUSES,
} from './contactFundOpportunityKanbanTypes';

const INVESTMENT_AMOUNT_FIELDS = [
  'indicativeInvestment',
  'preferredInvestment',
  'committedInvestment',
] as const;

function withNumericAmounts(investor: Investor): Investor {
  const parsed = { ...investor } as Record<string, unknown>;
  INVESTMENT_AMOUNT_FIELDS.forEach((field) => {
    if (parsed[field]) parsed[field] = parseInt(String(parsed[field]), 10);
  });
  return parsed as unknown as Investor;
}

function belongsOnKanban(investor: Investor): boolean {
  const status = investor.status as string | undefined;
  if (status === 'CANDIDATE' || status === 'LEAD' || status === 'OPPORTUNITY') return true;
  const entityAllocations = (
    investor as {
      entityAllocations?: Array<{ contactRole?: string; allocatedAmount?: number | string }>;
    }
  ).entityAllocations;
  return (
    entityAllocations?.some(
      (allocation) =>
        allocation?.contactRole === 'OWNER' && Number(allocation?.allocatedAmount || 0) > 0,
    ) ?? false
  );
}

/**
 * The pipeline funds this contact is an investor on, each with that investor record. One call for
 * the funds and one for the contact's own investments — not every fund's full investor list.
 */
export async function loadContactFundOpportunities(
  contactId: string,
  service = new BackendService(),
): Promise<FundOpportunityWithInvestor[]> {
  const [opportunities, investments] = await Promise.all([
    service.getFundOpportunityByStatus([...PIPELINE_STATUSES]),
    service.getContactInvestments(contactId),
  ]);

  const investorByFundId = new Map<string, Investor>();
  for (const investor of investments) {
    const fundId = investor.fundOpportunity?.id;
    if (fundId && !investorByFundId.has(fundId)) investorByFundId.set(fundId, investor);
  }

  return opportunities.flatMap((opportunity) => {
    const investor = investorByFundId.get(opportunity.id);
    if (!investor || !belongsOnKanban(investor)) return [];
    return [
      {
        ...opportunity,
        investor: withNumericAmounts(investor),
      } as FundOpportunityWithInvestor,
    ];
  });
}
